import { useState, useEffect } from 'react';
import { ConsistencyCollectionView } from '../types';

type UseEditCollectionModalParams = {
  collection: ConsistencyCollectionView | undefined;
  visible: boolean;
  onClose: VoidFunction;
  onCollectionUpdated: (
    id: string,
    params: { name: string; description: string; labels: string[] }
  ) => Promise<void>;
};

export const useEditCollectionModal = ({
  collection,
  visible,
  onClose,
  onCollectionUpdated,
}: UseEditCollectionModalParams) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [labels, setLabels] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [nameError, setNameError] = useState<string | undefined>();

  useEffect(() => {
    if (visible && collection) {
      setName(collection.name);
      setDescription(collection.description ?? '');
      setLabels(collection.labels);
      setNameError(undefined);
    }
  }, [visible, collection]);

  const handleNameChange = (value: string) => {
    setName(value);
    if (nameError) setNameError(undefined);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setNameError('Please enter a collection name');
      return;
    }
    if (!collection) return;

    setSubmitting(true);
    try {
      await onCollectionUpdated(collection.externalId, {
        name: name.trim(),
        description,
        labels,
      });
      onClose();
    } catch (err: unknown) {
      setNameError(
        err instanceof Error ? err.message : 'Failed to update collection'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return {
    name,
    handleNameChange,
    description,
    setDescription,
    labels,
    setLabels,
    submitting,
    nameError,
    handleSubmit,
  };
};
